import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router';
import { NavLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Box,
  Drawer,
  useMediaQuery,
  List,
  Typography,
  ListItemButton,
  Collapse,
  ListItemIcon,
  ListItemText,
  Paper,
  Avatar,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { SidebarWidth } from '../../../assets/global/Theme-variable';
import LogoIcon from '../logo/LogoIcon';
import { getMenuitems } from './Menuitems';

/* ---------- Shared styles (flat colors only, compact) ---------- */

const navItemSx = (active, level = 0) => ({
  flexShrink: 0,
  minHeight: level ? 34 : 40,
  py: level ? 0.5 : 0.75,
  px: 1.25,
  borderRadius: 1.5,
  borderLeft: '3px solid',
  borderColor: active ? 'primary.main' : 'transparent',
  color: active ? 'primary.main' : level ? 'text.secondary' : 'text.primary',
  bgcolor: active ? (t) => alpha(t.palette.primary.main, level ? 0.12 : 0.1) : 'transparent',
  transition: 'background-color .15s, color .15s',
  '&.Mui-selected, &.Mui-selected:hover': {
    bgcolor: (t) => alpha(t.palette.primary.main, level ? 0.12 : 0.1),
  },
  // Hover only on devices that really hover (avoids "sticky" hover on touch screens)
  '@media (hover: hover)': {
    '&:hover': {
      bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
      color: 'primary.main',
    },
  },
});

const iconSx = (active, size = 32) => ({
  minWidth: size,
  color: active ? 'primary.main' : 'text.secondary',
});

const scrollAreaSx = {
  flex: '1 1 auto',
  minHeight: 0, // required so a flex child can shrink and scroll
  overflowY: 'auto',
  overflowX: 'hidden',
  overscrollBehavior: 'contain',
  WebkitOverflowScrolling: 'touch',
  px: 1.5,
  py: 1,
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(128,128,128,.4) transparent',
  '&::-webkit-scrollbar': { width: 6 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(128,128,128,.35)',
    borderRadius: 3,
  },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(128,128,128,.55)' },
};

const paperSx = {
  width: SidebarWidth,
  maxWidth: '85vw',
  height: '100%',
  boxSizing: 'border-box',
  border: '0 !important',
  overflow: 'hidden', // only the inner nav area scrolls
};

/* ---------- Component ---------- */

const Sidebar = ({ isMobileSidebarOpen, onSidebarClose, isSidebarOpen, lst_menu, loggedUser }) => {
  const [open, setOpen] = useState(null);
  const { pathname } = useLocation();
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up('lg'));

  const pathWithoutLastPart = useMemo(
    () => pathname.slice(0, pathname.lastIndexOf('/')),
    [pathname],
  );

  const Menuitems = useMemo(() => {
    if (!lst_menu) return [];
    const items = getMenuitems(lst_menu, loggedUser);
    return Array.isArray(items) ? items : [];
  }, [lst_menu, loggedUser]);

  const handleClick = useCallback((index) => {
    setOpen((prev) => (prev === index ? null : index));
  }, []);

  // Close the drawer after navigating, on mobile only
  // (on desktop the persistent sidebar must stay open)
  const handleNavigate = useCallback(() => {
    if (!lgUp && onSidebarClose) onSidebarClose();
  }, [lgUp, onSidebarClose]);

  // Expand the group matching the current route
  useEffect(() => {
    const activeIndex = Menuitems.findIndex(
      (item) =>
        item.children &&
        (item.children.some((child) => child.href === pathname) ||
          pathWithoutLastPart === item.href),
    );
    if (activeIndex !== -1) setOpen(activeIndex);
  }, [pathname, pathWithoutLastPart, Menuitems]);

  const displayName = loggedUser?.prenom
    ? `${loggedUser.prenom} ${loggedUser.nom || ''}`.trim()
    : 'Utilisateur';

  const SidebarContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        minHeight: 0,
      }}
    >
      {/* Header / logo (fixed) */}
      <Box
        sx={{
          flexShrink: 0,
          px: 2,
          pt: { xs: 'max(12px, env(safe-area-inset-top))', lg: 2 },
          pb: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& svg, & img': { maxWidth: '100%', height: 'auto' },
        }}
      >
        <LogoIcon />
      </Box>

      <Divider sx={{ mx: 2, flexShrink: 0, opacity: 0.6 }} />

      {/* Scrollable navigation */}
      <Box component="nav" sx={scrollAreaSx}>
        <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {Menuitems.map((item, index) => {
            /* Sub header */
            if (item.subheader) {
              return (
                <Box key={`sub-${item.subheader}`} sx={{ mt: 2, mb: 0.5, px: 1 }}>
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      display: 'block',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.8px',
                      color: 'text.secondary',
                      fontSize: '0.68rem',
                    }}
                  >
                    {item.subheader}
                  </Typography>
                </Box>
              );
            }

            /* Parent with children */
            if (item.children) {
              const isParentSelected =
                pathWithoutLastPart === item.href ||
                item.children.some((child) => child.href === pathname);
              const isExpanded = open === index;

              return (
                <React.Fragment key={item.title}>
                  <ListItemButton
                    onClick={() => handleClick(index)}
                    selected={isParentSelected}
                    aria-expanded={isExpanded}
                    sx={navItemSx(isParentSelected)}
                  >
                    <ListItemIcon sx={iconSx(isParentSelected)}>
                      <FeatherIcon icon={item.icon} width="18" height="18" />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      sx={{ minWidth: 0, my: 0 }}
                      primaryTypographyProps={{
                        variant: 'body2',
                        noWrap: true,
                        fontWeight: isParentSelected ? 700 : 500,
                      }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        flexShrink: 0,
                        color: isParentSelected ? 'primary.main' : 'text.secondary',
                        transition: 'transform .2s',
                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      }}
                    >
                      <FeatherIcon icon="chevron-right" size="16" />
                    </Box>
                  </ListItemButton>

                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List
                      component="div"
                      disablePadding
                      sx={{ pl: 1.5, pt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}
                    >
                      {item.children.map((child) => {
                        const isChildSelected = pathname === child.href;
                        return (
                          <ListItemButton
                            key={child.title}
                            component={NavLink}
                            to={child.href}
                            onClick={handleNavigate}
                            selected={isChildSelected}
                            sx={navItemSx(isChildSelected, 1)}
                          >
                            <ListItemIcon sx={iconSx(isChildSelected, 26)}>
                              <FeatherIcon icon={child.icon || 'circle'} width="14" height="14" />
                            </ListItemIcon>
                            <ListItemText
                              primary={child.title}
                              sx={{ minWidth: 0, my: 0 }}
                              primaryTypographyProps={{
                                variant: 'body2',
                                noWrap: true,
                                fontWeight: isChildSelected ? 700 : 400,
                                fontSize: '0.82rem',
                              }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            }

            /* Single item */
            const isSelected = pathname === item.href;
            return (
              <ListItemButton
                key={item.title}
                component={NavLink}
                to={item.href}
                onClick={handleNavigate}
                selected={isSelected}
                sx={navItemSx(isSelected)}
              >
                <ListItemIcon sx={iconSx(isSelected)}>
                  <FeatherIcon icon={item.icon} width="18" height="18" />
                </ListItemIcon>
                <ListItemText
                  primary={item.title}
                  sx={{ minWidth: 0, my: 0 }}
                  primaryTypographyProps={{
                    variant: 'body2',
                    noWrap: true,
                    fontWeight: isSelected ? 700 : 500,
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>

      {/* User card (fixed footer, hidden on very short screens) */}
      {loggedUser && (
        <Box
          sx={{
            flexShrink: 0,
            p: 1.25,
            pb: 'max(10px, env(safe-area-inset-bottom))',
            '@media (max-height: 520px)': { display: 'none' },
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
              borderColor: (t) => alpha(t.palette.primary.main, 0.15),
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  bgcolor: 'primary.main',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                }}
              >
                {loggedUser?.prenom ? loggedUser.prenom.charAt(0).toUpperCase() : 'U'}
              </Avatar>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography variant="subtitle2" fontWeight={700} noWrap>
                  {displayName}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap display="block">
                  {loggedUser?.role || 'Membre'}
                </Typography>
              </Box>
              <Chip
                size="small"
                label="En ligne"
                color="success"
                sx={{
                  flexShrink: 0,
                  height: 18,
                  fontSize: '0.62rem',
                  fontWeight: 700,
                  borderRadius: 1,
                }}
              />
            </Stack>
          </Paper>
        </Box>
      )}
    </Box>
  );

  if (lgUp) {
    return (
      <Drawer
        anchor="left"
        open={isSidebarOpen}
        variant="persistent"
        PaperProps={{ sx: { ...paperSx, boxShadow: '1px 0 10px rgba(0,0,0,0.05)' } }}
      >
        {SidebarContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      anchor="left"
      open={isMobileSidebarOpen}
      onClose={onSidebarClose}
      variant="temporary"
      ModalProps={{ keepMounted: true }} // faster open on mobile
      PaperProps={{ sx: { ...paperSx, height: '100dvh' } }}
    >
      {SidebarContent}
    </Drawer>
  );
};

Sidebar.propTypes = {
  isMobileSidebarOpen: PropTypes.bool,
  onSidebarClose: PropTypes.func,
  isSidebarOpen: PropTypes.bool,
  lst_menu: PropTypes.array,
  loggedUser: PropTypes.object,
};

export default Sidebar;